import { useRef, useState } from "react";
import { Paperclip, FolderOpen, Camera, X, FileText, FileSpreadsheet, Image as ImageIcon } from "lucide-react";

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.heic,.heif,.docx,.xlsx,.doc,.xls";
const MAX_BYTES = 25 * 1024 * 1024;
const IMG_EXT = /\.(png|jpe?g|gif|webp|heic|heif)$/i;

export function FileGlyph({ name = "", className = "" }) {
  if (IMG_EXT.test(name)) return <ImageIcon className={className} />;
  if (/\.(xlsx?|csv)$/i.test(name)) return <FileSpreadsheet className={className} />;
  return <FileText className={className} />;
}

export function humanSize(bytes = 0) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Drag-and-drop + browse + camera attachment picker used on the CREATE form,
 * before a report id exists. Collects File objects (with an optional description)
 * in parent state; the parent uploads them after the report is created.
 */
export default function AttachmentPicker({ files, onChange }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const cameraRef = useRef(null);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    const accepted = [];
    for (const f of incoming) {
      if (f.size > MAX_BYTES) {
        // eslint-disable-next-line no-alert
        alert(`"${f.name}" is larger than 25 MB and was skipped.`);
        continue;
      }
      accepted.push({ file: f, description: "", preview: IMG_EXT.test(f.name) ? URL.createObjectURL(f) : null });
    }
    if (accepted.length) onChange([...(files || []), ...accepted]);
  };

  const removeAt = (idx) => onChange(files.filter((_, i) => i !== idx));
  const setDesc = (idx, description) =>
    onChange(files.map((f, i) => (i === idx ? { ...f, description } : f)));

  return (
    <div className="space-y-3" data-testid="attachment-picker">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
        className={`rounded-lg border-2 border-dashed p-5 text-center transition-colors ${dragging ? "border-[#00305E] bg-[#00305E]/5" : "border-gray-300 bg-gray-50/60"}`}
      >
        <Paperclip className="w-7 h-7 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-600 mb-3">Drag &amp; drop files here, or</p>
        <div className="flex flex-wrap gap-2 justify-center">
          <button type="button" onClick={() => inputRef.current?.click()} data-testid="attachment-browse-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-[#00305E] text-white hover:bg-[#00305E]/90">
            <FolderOpen className="w-4 h-4" />Browse files
          </button>
          <button type="button" onClick={() => cameraRef.current?.click()} data-testid="attachment-camera-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50">
            <Camera className="w-4 h-4" />Take photo
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">Images (JPG, PNG, HEIC) &amp; documents (PDF, DOCX, XLSX) · max 25 MB each</p>
        <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      </div>

      {(files || []).length > 0 && (
        <div className="space-y-2">
          {files.map((f, idx) => (
            <div key={idx} className="flex items-center gap-3 p-2 bg-white border rounded-lg" data-testid={`pending-attachment-${idx}`}>
              {f.preview ? (
                <img src={f.preview} alt={f.file.name} className="w-12 h-12 object-cover rounded" />
              ) : (
                <div className="w-12 h-12 flex items-center justify-center rounded bg-gray-100 text-gray-500">
                  <FileGlyph name={f.file.name} className="w-5 h-5" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{f.file.name}</p>
                <p className="text-xs text-gray-400">{humanSize(f.file.size)}</p>
                <input value={f.description} onChange={(e) => setDesc(idx, e.target.value)}
                  placeholder="Optional description (e.g. Photo of injury)"
                  className="mt-1 w-full text-xs border rounded px-2 py-1" data-testid={`pending-attachment-desc-${idx}`} />
              </div>
              <button type="button" onClick={() => removeAt(idx)} className="text-red-400 hover:text-red-600 px-1" data-testid={`pending-attachment-remove-${idx}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { IMG_EXT };
